import axios, { AxiosResponse, AxiosError } from 'axios';
import { AuthResponse, User, Animal, Farm, Stable, Box, LivestockReport, Treatment, FeedInventory, Machine, MachineUsage, Cost, Delivery, PaginatedResponse, ApiResponse, MasterDataCategory, MasterDataValue, Death, Slaughter, ContractTemplate, Medicine } from '../types';
import { Language, TranslationResponse } from '../types/language';

// API Base URL - exported for use in components that need direct fetch calls
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard API
export interface LowStockItem {
  id: number;
  feed_name: string;
  current_quantity: number;
  minimum_alert: number;
  unit: string;
}

export interface DashboardStats {
  active_animals: number;
  farms_count: number;
  avg_daily_weight_gain: number;
  total_estimated_weight: number;
  slaughter_ready_count: number;
  avg_cost_per_animal_per_day: number;
  active_period: {
    id: number;
    name: string;
    start_date: string;
    end_date: string;
  } | null;
  period_costs: {
    approved: number;
    total: number;
  } | null;
  alerts: {
    animals_under_withdrawal: number;
    low_stock_alerts: number;
    low_stock_items: LowStockItem[];
  };
}

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response: AxiosResponse<DashboardStats> = await api.get('/dashboard/stats');
    return response.data;
  },
};

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/login', { email, password });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/logout');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  me: async (): Promise<User> => {
    const response: AxiosResponse<User> = await api.get('/me');
    return response.data;
  },
};

// Farm API
export const farmApi = {
  getAll: async (): Promise<Farm[]> => {
    const response: AxiosResponse<Farm[]> = await api.get('/farms');
    return response.data;
  },

  getById: async (id: number): Promise<Farm> => {
    const response: AxiosResponse<Farm> = await api.get(`/farms/${id}`);
    return response.data;
  },

  getFarmMap: async (id: number) => {
    const response = await api.get(`/farms/${id}/map`);
    return response.data;
  },

  create: async (farm: Partial<Farm>): Promise<Farm> => {
    const response: AxiosResponse<Farm> = await api.post('/farms', farm);
    return response.data;
  },

  update: async (id: number, farm: Partial<Farm>): Promise<Farm> => {
    const response: AxiosResponse<Farm> = await api.put(`/farms/${id}`, farm);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/farms/${id}`);
  },
};

// Stable API
export const stableApi = {
  getAll: async (farmId: number): Promise<Stable[]> => {
    const response: AxiosResponse<Stable[]> = await api.get(`/farms/${farmId}/stables`);
    return response.data;
  },

  create: async (farmId: number, stable: Partial<Stable>): Promise<Stable> => {
    const response: AxiosResponse<Stable> = await api.post(`/farms/${farmId}/stables`, stable);
    return response.data;
  },

  update: async (farmId: number, id: number, stable: Partial<Stable>): Promise<Stable> => {
    const response: AxiosResponse<Stable> = await api.put(`/farms/${farmId}/stables/${id}`, stable);
    return response.data;
  },

  delete: async (farmId: number, id: number): Promise<void> => {
    await api.delete(`/farms/${farmId}/stables/${id}`);
  },
};

// Box API
export const boxApi = {
  getAll: async (farmId: number): Promise<Box[]> => {
    const response: AxiosResponse<Box[]> = await api.get(`/farms/${farmId}/boxes`);
    return response.data;
  },

  create: async (farmId: number, box: Partial<Box>): Promise<Box> => {
    const response: AxiosResponse<Box> = await api.post(`/farms/${farmId}/boxes`, box);
    return response.data;
  },

  update: async (farmId: number, id: number, box: Partial<Box>): Promise<Box> => {
    const response: AxiosResponse<Box> = await api.put(`/farms/${farmId}/boxes/${id}`, box);
    return response.data;
  },

  delete: async (farmId: number, id: number): Promise<void> => {
    await api.delete(`/farms/${farmId}/boxes/${id}`);
  },
};

// Animal API
export const animalApi = {
  getAll: async (params?: any): Promise<{ data: Animal[]; current_page: number; last_page: number; per_page: number; total: number }> => {
    const response = await api.get('/animals', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Animal> => {
    const response: AxiosResponse<Animal> = await api.get(`/animals/${id}`);
    return response.data;
  },

  create: async (animal: Partial<Animal>): Promise<Animal> => {
    const response: AxiosResponse<Animal> = await api.post('/animals', animal);
    return response.data;
  },

  update: async (id: number, animal: Partial<Animal>): Promise<Animal> => {
    const response: AxiosResponse<Animal> = await api.put(`/animals/${id}`, animal);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/animals/${id}`);
  },

  getAnimalCard: async (id: number) => {
    const response = await api.get(`/animals/${id}/card`);
    return response.data;
  },

  moveAnimal: async (id: number, data: { to_box_id?: number; movement_type: string; reason?: string }) => {
    const response = await api.post(`/animals/${id}/move`, data);
    return response.data;
  },

  importAnimals: async (file: File): Promise<{ message: string; success_count: number; error_count: number; errors: any[] }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/animals/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  exportAnimals: async (params?: Record<string, any>): Promise<{ blob: Blob; filename: string; exportCount: number }> => {
    const response = await api.get('/animals/export', {
      params,
      responseType: 'blob',
      timeout: 30000,
    });

    // Extract filename from Content-Disposition header, fallback to default
    const disposition = response.headers['content-disposition'];
    let filename = `allatok_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      if (match?.[2]) {
        filename = decodeURIComponent(match[2]);
      }
    }

    const exportCount = parseInt(response.headers['x-export-count'] || '0', 10);

    return { blob: response.data, filename, exportCount };
  },
};

// Medicine catalog API
export const medicineApi = {
  getAll: async (): Promise<Medicine[]> => {
    const response: AxiosResponse<Medicine[]> = await api.get('/medicines');
    return response.data;
  },
};

// Treatment API
export const treatmentApi = {
  getAll: async (params?: any): Promise<PaginatedResponse<Treatment>> => {
    const response = await api.get('/treatments', { params });
    return response.data;
  },

  create: async (treatment: Partial<Treatment>): Promise<Treatment> => {
    const response: AxiosResponse<Treatment> = await api.post('/treatments', treatment);
    return response.data;
  },

  boxBulkTreatment: async (data: any): Promise<any> => {
    const response = await api.post('/treatments/box-bulk', data);
    return response.data;
  },

  getAnimalTreatments: async (animalId: number): Promise<Treatment[]> => {
    const response: AxiosResponse<Treatment[]> = await api.get(`/animals/${animalId}/treatments`);
    return response.data;
  },
};

// Inventory API
export const inventoryApi = {
  getMedicines: async (farmId?: number, availableOnly?: boolean) => {
    const params: any = {};
    if (farmId) params.farm_id = farmId;
    if (availableOnly) params.available_only = true;
    const response = await api.get('/inventory/medicines', { params });
    return response.data;
  },

  getFeeds: async (farmId?: number, availableOnly?: boolean): Promise<FeedInventory[]> => {
    const params: any = {};
    if (farmId) params.farm_id = farmId;
    if (availableOnly) params.available_only = true;
    const response: AxiosResponse<FeedInventory[]> = await api.get('/inventory/feeds', { params });
    return response.data;
  },

  getAlerts: async (farmId?: number) => {
    const response = await api.get('/inventory/alerts', {
      params: farmId ? { farm_id: farmId } : undefined,
    });
    return response.data;
  },

  addMedicine: async (data: any) => {
    const response = await api.post('/inventory/medicines', data);
    return response.data;
  },

  addFeed: async (data: any) => {
    const response = await api.post('/inventory/feeds', data);
    return response.data;
  },

  addBales: async (data: any) => {
    const response = await api.post('/inventory/bales', data);
    return response.data;
  },

  addFuel: async (data: any) => {
    const response = await api.post('/inventory/fuel', data);
    return response.data;
  },

  getFeedTransactions: async (farmId?: number) => {
    const response = await api.get('/inventory/feed-transactions', {
      params: farmId ? { farm_id: farmId } : undefined,
    });
    return response.data;
  },

  getMedicineTransactions: async (farmId?: number) => {
    const response = await api.get('/inventory/medicine-transactions', {
      params: farmId ? { farm_id: farmId } : undefined,
    });
    return response.data;
  },

  deleteFeedTransaction: async (id: number) => {
    const response = await api.delete(`/inventory/feed-transactions/${id}`);
    return response.data;
  },

  updateFeedTransaction: async (id: number, data: any) => {
    const response = await api.put(`/inventory/feed-transactions/${id}`, data);
    return response.data;
  },

  deleteMedicineTransaction: async (id: number) => {
    const response = await api.delete(`/inventory/medicine-transactions/${id}`);
    return response.data;
  },

  updateMedicineTransaction: async (id: number, data: any) => {
    const response = await api.put(`/inventory/medicine-transactions/${id}`, data);
    return response.data;
  },
};

// Reports API
export const reportsApi = {
  getLivestockReport: async (params?: { farm_id?: number; box_id?: number; from_date?: string; to_date?: string }): Promise<LivestockReport> => {
    const response: AxiosResponse<LivestockReport> = await api.get('/reports/livestock', { params });
    return response.data;
  },

  getFinancialReport: async (params?: { farm_id?: number; box_id?: number; from_date?: string; to_date?: string }) => {
    const response = await api.get('/reports/financial', { params });
    return response.data;
  },

  getHealthReport: async (params?: { farm_id?: number; box_id?: number; from_date?: string; to_date?: string }) => {
    const response = await api.get('/reports/health', { params });
    return response.data;
  },

  getWithdrawalCalendar: async (params?: { farm_id?: number; box_id?: number; days?: number }) => {
    const response = await api.get('/reports/withdrawal-calendar', { params });
    return response.data;
  },
};

// Machine API
export const machineApi = {
  getAll: async (params?: any): Promise<{ data: Machine[] }> => {
    const response = await api.get('/machines', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Machine> => {
    const response: AxiosResponse<Machine> = await api.get(`/machines/${id}`);
    return response.data;
  },

  create: async (machine: Partial<Machine>): Promise<Machine> => {
    const response: AxiosResponse<Machine> = await api.post('/machines', machine);
    return response.data;
  },

  update: async (id: number, machine: Partial<Machine>): Promise<Machine> => {
    const response: AxiosResponse<Machine> = await api.put(`/machines/${id}`, machine);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/machines/${id}`);
  },

  recordUsage: async (id: number, usage: Partial<MachineUsage>): Promise<MachineUsage> => {
    const response: AxiosResponse<MachineUsage> = await api.post(`/machines/${id}/usage`, usage);
    return response.data;
  },

  getUsage: async (id: number, params?: any): Promise<MachineUsage[]> => {
    const response: AxiosResponse<MachineUsage[]> = await api.get(`/machines/${id}/usage`, { params });
    return response.data;
  },
};

// Cost API
export const costApi = {
  getAll: async (params?: any): Promise<{ data: Cost[] }> => {
    const response = await api.get('/costs', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Cost> => {
    const response: AxiosResponse<Cost> = await api.get(`/costs/${id}`);
    return response.data;
  },

  create: async (cost: Partial<Cost>): Promise<Cost> => {
    const response: AxiosResponse<Cost> = await api.post('/costs', cost);
    return response.data;
  },

  update: async (id: number, cost: Partial<Cost>): Promise<Cost> => {
    const response: AxiosResponse<Cost> = await api.put(`/costs/${id}`, cost);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/costs/${id}`);
  },

  approve: async (id: number): Promise<Cost> => {
    const response: AxiosResponse<Cost> = await api.post(`/costs/${id}/approve`);
    return response.data;
  },

  getAnimalCosts: async (animalId: number): Promise<Cost[]> => {
    const response: AxiosResponse<Cost[]> = await api.get(`/costs/allocation/${animalId}`);
    return response.data;
  },
};

// Language API
export const languageApi = {
  getAll: async (): Promise<Language[]> => {
    const response: AxiosResponse<Language[]> = await api.get('/languages');
    return response.data;
  },

  getById: async (id: number): Promise<Language> => {
    const response: AxiosResponse<Language> = await api.get(`/languages/${id}`);
    return response.data;
  },

  getTranslations: async (languageCode: string, group?: string): Promise<TranslationResponse> => {
    const params: any = { language: languageCode };
    if (group) {
      params.group = group;
    }
    const response: AxiosResponse<TranslationResponse> = await api.get('/translations', { params });
    return response.data;
  },

  getTranslationsByLanguage: async (languageCode: string): Promise<TranslationResponse> => {
    const response: AxiosResponse<TranslationResponse> = await api.get(`/translations/${languageCode}`);
    return response.data;
  },

  getTranslationsByGroup: async (languageCode: string, group: string): Promise<TranslationResponse> => {
    const response: AxiosResponse<TranslationResponse> = await api.get(`/translations/${languageCode}/${group}`);
    return response.data;
  },
};

// Feeding API
export const feedingApi = {
  getAll: async (params?: any): Promise<{ data: any[] }> => {
    const response = await api.get('/feedings', { params });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/feedings/${id}`);
    return response.data;
  },

  create: async (feeding: any): Promise<any> => {
    const response = await api.post('/feedings', feeding);
    return response.data;
  },

  update: async (id: number, feeding: any): Promise<any> => {
    const response = await api.put(`/feedings/${id}`, feeding);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/feedings/${id}`);
  },

  getAnimalFeedings: async (animalId: number): Promise<any[]> => {
    const response = await api.get(`/animals/${animalId}/feedings`);
    return response.data;
  },

  getBoxFeedings: async (boxId: number): Promise<any[]> => {
    const response = await api.get(`/boxes/${boxId}/feedings`);
    return response.data;
  },
};

// User Management API
export const userApi = {
  getAll: async (): Promise<User[]> => {
    const response: AxiosResponse<User[]> = await api.get('/admin/users');
    return response.data;
  },

  getById: async (id: number): Promise<User> => {
    const response: AxiosResponse<User> = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  create: async (user: Partial<User>): Promise<User> => {
    const response: AxiosResponse<User> = await api.post('/admin/users', user);
    return response.data;
  },

  update: async (id: number, user: Partial<User>): Promise<User> => {
    const response: AxiosResponse<User> = await api.put(`/admin/users/${id}`, user);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/users/${id}`);
  },

  toggleActive: async (id: number): Promise<User> => {
    const response: AxiosResponse<User> = await api.patch(`/admin/users/${id}/toggle-active`);
    return response.data;
  },

  resetPassword: async (id: number): Promise<{ password: string }> => {
    const response: AxiosResponse<{ password: string }> = await api.post(`/admin/users/${id}/reset-password`);
    return response.data;
  },
};

// Delivery API
export const deliveryApi = {
  getAll: async (params?: { farm_id?: number; page?: number; type?: string; search?: string }): Promise<PaginatedResponse<Delivery>> => {
    const response: AxiosResponse<PaginatedResponse<Delivery>> = await api.get('/deliveries', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Delivery> => {
    const response: AxiosResponse<Delivery> = await api.get(`/deliveries/${id}`);
    return response.data;
  },

  create: async (delivery: Partial<Delivery>): Promise<Delivery> => {
    const response: AxiosResponse<Delivery> = await api.post('/deliveries', delivery);
    return response.data;
  },

  update: async (id: number, delivery: Partial<Delivery>): Promise<Delivery> => {
    const response: AxiosResponse<Delivery> = await api.put(`/deliveries/${id}`, delivery);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/deliveries/${id}`);
  },

  addAnimals: async (id: number, animalIds: number[]): Promise<ApiResponse<Delivery>> => {
    const response: AxiosResponse<ApiResponse<Delivery>> = await api.post(`/deliveries/${id}/animals`, {
      animal_ids: animalIds
    });
    return response.data;
  },

  getDeliveryDocuments: async (deliveryId: number): Promise<any> => {
    const response = await api.get(`/deliveries/${deliveryId}/documents`);
    return response.data;
  },

  uploadDeliveryDocument: async (deliveryId: number, formData: FormData): Promise<any> => {
    const response = await api.post(`/deliveries/${deliveryId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  bulkCreateAnimals: async (deliveryId: number, bulkData: { ear_tags: string[], common_data: any }): Promise<{ animals: any[] }> => {
    const response: AxiosResponse<{ animals: any[] }> = await api.post(`/deliveries/${deliveryId}/bulk-create-animals`, bulkData);
    return response.data;
  },

  updateDeliveryAnimal: async (deliveryId: number, animalId: number, animalData: any): Promise<Animal> => {
    const response: AxiosResponse<Animal> = await api.patch(`/deliveries/${deliveryId}/animals/${animalId}`, animalData);
    return response.data;
  },

  finalizeDelivery: async (deliveryId: number): Promise<Delivery> => {
    const response: AxiosResponse<Delivery> = await api.post(`/deliveries/${deliveryId}/finalize`);
    return response.data;
  },

  exportAnimals: async (deliveryId: number): Promise<{ blob: Blob; filename: string; exportCount: number }> => {
    const response = await api.get(`/deliveries/${deliveryId}/animals/export`, {
      responseType: 'blob',
      timeout: 30000,
    });

    const disposition = response.headers['content-disposition'];
    let filename = `kiszallitas_allatok_${deliveryId}_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]*)\1/);
      if (match?.[2]) {
        filename = decodeURIComponent(match[2]);
      }
    }

    const exportCount = parseInt(response.headers['x-export-count'] || '0', 10);

    return { blob: response.data, filename, exportCount };
  }
};

// Master Data API
export const masterDataApi = {
  getCategory: async (categoryName: string): Promise<MasterDataCategory> => {
    const response: AxiosResponse<{ category: MasterDataCategory }> = await api.get(`/master-data/category/${categoryName}`);
    return response.data.category;
  },

  getCategories: async (): Promise<MasterDataCategory[]> => {
    const response: AxiosResponse<{ categories: MasterDataCategory[] }> = await api.get('/master-data/categories');
    return response.data.categories;
  },

  getCategoryValues: async (categoryId: number): Promise<MasterDataValue[]> => {
    const response: AxiosResponse<{ values: MasterDataValue[] }> = await api.get(`/master-data/categories/${categoryId}/values`);
    return response.data.values;
  },

  createCategory: async (category: any): Promise<any> => {
    const response = await api.post('/admin/master-data/categories', category);
    return response.data;
  },

  updateCategory: async (categoryId: number, category: any): Promise<any> => {
    const response = await api.put(`/admin/master-data/categories/${categoryId}`, category);
    return response.data;
  },

  deleteCategory: async (categoryId: number): Promise<void> => {
    await api.delete(`/admin/master-data/categories/${categoryId}`);
  },

  createValue: async (categoryId: number, data: Partial<MasterDataValue>): Promise<MasterDataValue> => {
    const response: AxiosResponse<{ value: MasterDataValue }> = await api.post(`/master-data/categories/${categoryId}/values`, data);
    return response.data.value;
  },

  updateValue: async (valueId: number, data: Partial<MasterDataValue>): Promise<MasterDataValue> => {
    const response: AxiosResponse<{ value: MasterDataValue }> = await api.put(`/admin/master-data/values/${valueId}`, data);
    return response.data.value;
  },

  deleteValue: async (valueId: number): Promise<void> => {
    await api.delete(`/admin/master-data/values/${valueId}`);
  },
};

export const animalFormOptionsApi = {
  getBreeds: async (): Promise<MasterDataValue[]> => {
    try {
      const category = await masterDataApi.getCategory('animal_breeds');
      return category.values ?? [];
    } catch {
      return [];
    }
  },

  getColors: async (): Promise<MasterDataValue[]> => {
    try {
      const category = await masterDataApi.getCategory('animal_colors');
      return category.values ?? [];
    } catch {
      return [];
    }
  },

  getCountries: async (): Promise<MasterDataValue[]> => {
    try {
      const category = await masterDataApi.getCategory('countries');
      return category.values ?? [];
    } catch {
      return [];
    }
  },

};

// Death API
export const deathApi = {
  getAll: async (params?: any): Promise<PaginatedResponse<Death>> => {
    const response = await api.get('/deaths', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Death> => {
    const response: AxiosResponse<Death> = await api.get(`/deaths/${id}`);
    return response.data;
  },

  create: async (death: Partial<Death>): Promise<Death> => {
    const response: AxiosResponse<Death> = await api.post('/deaths', death);
    return response.data;
  },

  update: async (id: number, death: Partial<Death>): Promise<Death> => {
    const response: AxiosResponse<Death> = await api.put(`/deaths/${id}`, death);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/deaths/${id}`);
  },
};

// Slaughter API
export const slaughterApi = {
  getAll: async (params?: any): Promise<PaginatedResponse<Slaughter>> => {
    const response = await api.get('/slaughters', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Slaughter> => {
    const response: AxiosResponse<Slaughter> = await api.get(`/slaughters/${id}`);
    return response.data;
  },

  create: async (slaughter: Partial<Slaughter>): Promise<Slaughter> => {
    const response: AxiosResponse<Slaughter> = await api.post('/slaughters', slaughter);
    return response.data;
  },

  update: async (id: number, slaughter: Partial<Slaughter>): Promise<Slaughter> => {
    const response: AxiosResponse<Slaughter> = await api.put(`/slaughters/${id}`, slaughter);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/slaughters/${id}`);
  },
};

export const documentApi = {
  getAnimalDocuments: async (animalId: number): Promise<any> => {
    const response = await api.get(`/animals/${animalId}/documents`);
    return response.data;
  },

  uploadAnimalDocument: async (animalId: number, formData: FormData): Promise<any> => {
    const response = await api.post(`/animals/${animalId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadDocument: async (documentId: number): Promise<Blob> => {
    const response = await api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  viewDocument: async (documentId: number): Promise<string> => {
    const response = await api.get(`/documents/${documentId}/view`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  deleteDocument: async (documentId: number): Promise<any> => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  },
};

// Contract Template API
export const contractApi = {
  getTemplates: async (): Promise<ContractTemplate[]> => {
    const response: AxiosResponse<ContractTemplate[]> = await api.get('/contract-templates');
    return response.data;
  },

  getTemplate: async (id: number): Promise<ContractTemplate> => {
    const response: AxiosResponse<ContractTemplate> = await api.get(`/contract-templates/${id}`);
    return response.data;
  },

  createTemplate: async (formData: FormData): Promise<ContractTemplate> => {
    const response: AxiosResponse<{ template: ContractTemplate }> = await api.post('/contract-templates', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.template;
  },

  updateTemplate: async (id: number, data: Partial<ContractTemplate>): Promise<ContractTemplate> => {
    const response: AxiosResponse<{ template: ContractTemplate }> = await api.put(`/contract-templates/${id}`, data);
    return response.data.template;
  },

  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/contract-templates/${id}`);
  },

  downloadTemplate: async (id: number): Promise<Blob> => {
    const response = await api.get(`/contract-templates/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getAvailableVariables: async (): Promise<Record<string, Record<string, string>>> => {
    const response = await api.get('/contract-variables');
    return response.data;
  },

  generateContract: async (deliveryId: number, templateId: number): Promise<Blob> => {
    const response = await api.post(`/deliveries/${deliveryId}/generate-contract`,
      { template_id: templateId },
      { responseType: 'blob' }
    );
    return response.data;
  },

  previewDeliveryData: async (deliveryId: number): Promise<any> => {
    const response = await api.get(`/deliveries/${deliveryId}/preview-data`);
    return response.data;
  },
};

export default api;